import {
  AttentionBlock,
  Button,
  PageHeader,
  PageTitle,
  SavedIndicator,
  Skeleton,
  StatusTag,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  formatDate,
  titleCase,
} from '@westhetics/ui'
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { LoadError } from '@/app/load-error'
import { Page } from '@/app/shell'
import { routes, type TemplateTab } from '@/routes'
import { useNextInReview, useTemplate, type Template } from './api'
import { AUDIENCE_LABELS, CATEGORY_LABELS, canLinkProcedures, templateStatus } from './labels'
import { TemplateClinicsTab } from './template-clinics-tab'
import { TemplateCompareTab } from './template-compare-tab'
import { TemplateDataTab } from './template-data-tab'
import { TemplateEditorTab, type SaveState } from './template-editor-tab'
import { TemplateHistoryTab } from './template-history-tab'
import { TemplateProceduresTab } from './template-procedures-tab'
import { TemplateVersionsTab } from './template-versions-tab'

/**
 * Um contrato ou termo (docs/areas/documentos-e-assinaturas.md, "Documento"). Arquétipo: ficha com
 * abas — Editor, Versões, Comparar, Procedimentos, Clínicas, Histórico e Dados; cada aba trata de
 * um assunto só. A ação primária do cabeçalho existe só quando há alteração por publicar. O que veio
 * do sistema anterior sem revisão jurídica diz isso acima das abas, com o próximo da fila (RN-DOC-53).
 */

const TABS: TemplateTab[] = [
  'editor',
  'versions',
  'compare',
  'procedures',
  'clinics',
  'history',
  'data',
]

export function TemplatePage() {
  const { id = '' } = useParams()
  // Um documento por montagem: ir ao próximo da fila não pode levar o editor (e a fila de salvamento) do anterior.
  return <TemplateScreen key={id} id={id} />
}

function TemplateScreen({ id }: { id: string }) {
  const { tab } = useParams()
  const navigate = useNavigate()
  const query = useTemplate(id)
  const [saveState, setSaveState] = useState<SaveState>({
    savedAt: null,
    saving: false,
    dirty: false,
  })

  const template = query.data
  const active: TemplateTab = TABS.includes(tab as TemplateTab) ? (tab as TemplateTab) : 'editor'

  return (
    <>
      <PageHeader
        LinkComponent={Link}
        crumbs={[
          { label: 'Contratos e termos', href: routes.admin.templates },
          { label: template?.name ?? '…' },
        ]}
        aside={
          active === 'editor' ? (
            <SavedIndicator
              savedAt={
                saveState.savedAt ?? (template?.draft ? new Date(template.draft.updatedAt) : null)
              }
              saving={saveState.saving}
              dirty={saveState.dirty}
            />
          ) : null
        }
        actions={
          template && template.state !== 'archived' && template.hasUnpublishedChanges ? (
            <Button size="sm" asChild>
              <Link to={routes.admin.publishTemplate(template.id)}>Publicar versão</Link>
            </Button>
          ) : null
        }
      />
      <Page full>
        {query.isError ? (
          <LoadError
            title="Não conseguimos abrir este documento"
            error={query.error}
            onRetry={() => void query.refetch()}
          />
        ) : !template ? (
          <div role="status" aria-label="Carregando" className="flex flex-col gap-4">
            <Skeleton className="h-[26px] w-[360px] max-w-full" />
            <Skeleton className="h-[13px] w-[520px] max-w-full" />
            <Skeleton className="mt-4 h-[420px] w-full" />
          </div>
        ) : (
          <>
            <PageTitle
              title={template.name}
              meta={identification(template)}
              trailing={
                <StatusTag level={templateStatus(template).level}>
                  {templateStatus(template).label}
                </StatusTag>
              }
            />
            <LegalReviewNotice template={template} />
            <Tabs
              className="mt-6"
              value={active}
              onValueChange={(value) =>
                navigate(routes.admin.template(template.id, value as TemplateTab))
              }
            >
              <TabsList>
                <TabsTrigger value="editor">Editor</TabsTrigger>
                <TabsTrigger value="versions">
                  Versões{template.versionCount ? ` ${template.versionCount}` : ''}
                </TabsTrigger>
                <TabsTrigger value="compare">Comparar</TabsTrigger>
                {canLinkProcedures(template) ? (
                  <TabsTrigger value="procedures">
                    Procedimentos
                    {template.procedures.length ? ` ${template.procedures.length}` : ''}
                  </TabsTrigger>
                ) : null}
                <TabsTrigger value="clinics">
                  Clínicas{template.usage.clinicCount ? ` ${template.usage.clinicCount}` : ''}
                </TabsTrigger>
                <TabsTrigger value="history">Histórico</TabsTrigger>
                <TabsTrigger value="data">Dados</TabsTrigger>
              </TabsList>
              <TabsContent value="editor">
                <TemplateEditorTab template={template} onSaveState={setSaveState} />
              </TabsContent>
              <TabsContent value="versions">
                <TemplateVersionsTab template={template} />
              </TabsContent>
              <TabsContent value="compare">
                <TemplateCompareTab template={template} />
              </TabsContent>
              {canLinkProcedures(template) ? (
                <TabsContent value="procedures">
                  <TemplateProceduresTab template={template} />
                </TabsContent>
              ) : null}
              <TabsContent value="clinics">
                <TemplateClinicsTab template={template} />
              </TabsContent>
              <TabsContent value="history">
                <TemplateHistoryTab template={template} />
              </TabsContent>
              <TabsContent value="data">
                <TemplateDataTab template={template} />
              </TabsContent>
            </Tabs>
          </>
        )}
      </Page>
    </>
  )
}

/** "Termo de consentimento · Pacientes · versão 2026.08 publicada em 12/08/2026 por Helena Prado · em 41 clínicas". */
function identification(template: Template) {
  const parts = [CATEGORY_LABELS[template.category], AUDIENCE_LABELS[template.audience]]
  const version = template.currentVersion
  if (version) {
    parts.push(
      `versão ${version.label} publicada em ${formatDate(new Date(version.publishedAt), { year: true })} por ${titleCase(version.publishedBy.name)}`,
    )
  } else parts.push('ainda sem versão publicada')
  const clinics = template.usage.clinicCount
  parts.push(clinics === 1 ? 'em 1 clínica' : `em ${clinics} clínicas`)
  return parts.join(' · ')
}

/**
 * A revisão jurídica que falta (RN-DOC-53). Em uso nas clínicas é risco (plum); rascunho que ainda não
 * chega a ninguém é contexto (lilás). Sai sozinho quando uma versão é publicada com quem revisou.
 */
function LegalReviewNotice({ template }: { template: Template }) {
  const isPending = template.isLegalReviewPending && template.state !== 'archived'
  const next = useNextInReview(template.id, isPending)
  if (!isPending) return null

  const version = template.currentVersion
  const legacy = template.legacy
  const clinics = template.usage.clinicCount
  const usage = [
    legacy?.activeClinics != null
      ? `${legacy.activeClinics === 1 ? '1 clínica tinha' : `${legacy.activeClinics} clínicas tinham`} o modelo ativo`
      : null,
    legacy?.sendsLast90Days != null
      ? `${legacy.sendsLast90Days === 1 ? '1 envio' : `${legacy.sendsLast90Days} envios`} nos 90 dias antes da exportação`
      : null,
  ].filter(Boolean)

  return (
    <AttentionBlock
      className="mt-5"
      variant={version ? 'plum' : 'lilac'}
      title={
        version ? 'Revisão jurídica pendente' : 'Do sistema anterior, ainda sem revisão jurídica'
      }
    >
      <p>
        {version
          ? `A versão ${version.label} foi publicada pela carga do sistema anterior, sem revisão jurídica, e ${
              clinics === 0
                ? 'ainda não está em nenhuma clínica'
                : clinics === 1
                  ? 'já vale em 1 clínica'
                  : `já vale em ${clinics} clínicas`
            }. Revise o texto no editor e publique uma versão registrando quem revisou: é isso que tira o documento da fila.`
          : `O texto veio do sistema anterior${legacy?.legacyName ? ` ("${legacy.legacyName}")` : ''} e ainda não chega às clínicas. Revise no editor e publique a primeira versão registrando quem revisou.`}
      </p>
      {usage.length > 0 ? (
        <p className="text-secondary mt-1 text-[12px]">No sistema anterior: {usage.join(' · ')}.</p>
      ) : null}
      {next.data ? (
        <p className="mt-2">
          <Link
            to={routes.admin.template(next.data.id, 'editor')}
            className="border-hairline-heavy hover:border-ink border-b pb-[2px]"
          >
            Próximo da fila: {next.data.name}
          </Link>
        </p>
      ) : null}
    </AttentionBlock>
  )
}
